import { useMutation, useQueryClient } from '@tanstack/react-query';
import { agreeMarketingAlarm } from 'shared/src/api/myPageService/agreeMarketingAlarm';
import { disagreeMarketingAlarm } from 'shared/src/api/myPageService/disagreeMarketingAlarm';
import { queryKey } from '../../../constant/queryKey';
import type { GetMemberInfoResponse } from 'shared/src/api/getMemberInfo/types';
import { bridge } from 'shared/src/utils';

type MarketingType = 'SMS' | 'PUSH' | 'NIGHT_PUSH';

type ToggleMarketingParams = {
  type: MarketingType;
  isAgreed: boolean;
};

const CHANNEL_LABEL: Record<MarketingType, string> = {
  SMS: '혜택 문자',
  PUSH: '혜택 푸시',
  NIGHT_PUSH: '야간 푸시',
};

// 처리 당일 날짜 yyyy.MM.dd (네이티브 수신동의 처리결과 안내 포맷)
const formatProcessedDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

/**
 * 광고성 정보 수신동의 처리결과 안내 문구 (정보통신망법 제50조)
 * - isAgreed=true(직전 상태 ON) → 철회 동작 문구
 * - isAgreed=false → 동의 동작 문구
 * - NIGHT_PUSH 동의 시 PUSH가 꺼져있었다면 함께 동의 처리되므로 안내 추가(iOS 패턴)
 */
const buildMarketingToastMessage = (
  type: MarketingType,
  isAgreed: boolean,
  wasPushAgreed: boolean,
): string => {
  const date = formatProcessedDate(new Date());

  if (isAgreed) {
    return `${date}에 에이블리 ${CHANNEL_LABEL[type]} 수신 동의를 철회했어요`;
  }

  if (type === 'NIGHT_PUSH' && !wasPushAgreed) {
    return `${date}에 에이블리 야간 푸시 수신에 동의했어요\n혜택 푸시 수신도 같이 동의 처리되어요.`;
  }

  return `${date}에 에이블리 ${CHANNEL_LABEL[type]} 수신에 동의했어요`;
};

export const useToggleMarketingAlarmSetting = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, isAgreed }: ToggleMarketingParams) => {
      if (isAgreed) {
        return await disagreeMarketingAlarm(type);
      } else {
        if (type === 'NIGHT_PUSH') {
          await agreeMarketingAlarm('PUSH');
        }
        return await agreeMarketingAlarm(type);
      }
    },

    onSuccess: async (_, { type, isAgreed }) => {
      const old = client.getQueryData<GetMemberInfoResponse>(
        queryKey.marketingAlarmSettings(),
      );
      if (!old) return;

      const agreedKeyMap: Record<
        MarketingType,
        keyof GetMemberInfoResponse
      > = {
        SMS: 'sms_agreed_at',
        PUSH: 'push_agreed_at',
        NIGHT_PUSH: 'night_push_agreed_at',
      };

      const key = agreedKeyMap[type];

      // NIGHT_PUSH 동의 시 PUSH 동반 안내 분기를 위해 직전 PUSH 동의 상태 기록
      const wasPushAgreed = old.push_agreed_at != null;

      const updated: GetMemberInfoResponse = {
        ...old,
        [key]: isAgreed ? null : new Date().toISOString(),
      };

      // NIGHT_PUSH를 ON할 때는 PUSH도 같이 ON 처리
      if (type === 'NIGHT_PUSH' && !isAgreed) {
        updated['push_agreed_at'] = new Date().toISOString();
      }

      // 캐시 업데이트
      client.setQueryData(queryKey.marketingAlarmSettings(), updated);

      // 네이티브 member 동기화
      const jsonString = JSON.stringify({ member: updated });
      await bridge.updateMemberMe(jsonString);

      // 수신동의 처리결과 안내 (native → RN 이관 중 누락된 기능 복원)
      bridge.toast(buildMarketingToastMessage(type, isAgreed, wasPushAgreed));
    },
  });
};
